<template>
  <div>
    <div class="row" style="row-gap:18px;">
      <component
        :is="kpi.href ? 'a' : 'div'"
        v-for="kpi in visibleKpis"
        :key="kpi.key"
        :href="kpi.href"
        class="col-6 col-md-4"
        :class="{ 'kpi-row--linked': kpi.href }"
      >
        <div class="text-secondary mb-1" style="font-size:12px;">{{ kpi.label }}</div>
        <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
          <span class="kpi-row__value" style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">
            {{ kpi.formatted }}
          </span>
          <span
            class="d-inline-flex align-items-center bg-light"
            :class="kpi.comparison ? trendTextClass(kpi) : 'text-secondary'"
            style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px;gap:4px;"
          >
            <i
              v-if="kpi.comparison"
              :class="kpi.comparison.direction === 'up' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"
              style="font-size:8px;"
            ></i>
            {{ kpi.comparison ? Math.abs(kpi.comparison.change_percent) + '%' : 'N/A' }}
          </span>
          <i v-if="kpi.href" class="bi bi-chevron-right text-secondary" style="font-size:10px;"></i>
        </div>
      </component>
    </div>

    <button
      v-if="restKpis.length"
      @click="expanded = !expanded"
      class="btn btn-sm btn-link text-secondary d-inline-flex align-items-center p-0 mt-3"
      style="font-size:12px;font-weight:500;gap:5px;text-decoration:none;"
    >
      {{ expanded ? 'Show fewer metrics' : `Show ${restKpis.length} more metrics` }}
      <i :class="expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" style="font-size:10px;"></i>
    </button>
  </div>
</template>

<script>
const PRIMARY_ORDER = [
  'gross_sales', 'invoice_sales', 'total_orders', 'pending_orders',
  'gross_profit', 'discount_total', 'refund_total'
];

// Only metrics with a real, working destination on /orders get linked —
// see AllOrders.vue / order.blade.php for which query params it actually reads.
const LINK_BUILDERS = {
  total_orders: (startDate, endDate) => `/orders?start-date=${startDate}&end-date=${endDate}`,
  pending_orders: (startDate, endDate) => `/orders?un-attended=1&start-date=${startDate}&end-date=${endDate}`,
  discount_total: (startDate, endDate) => `/orders?has-discount=1&start-date=${startDate}&end-date=${endDate}`,
  outstanding_balance: (startDate, endDate) => `/orders?has-balance=1&start-date=${startDate}&end-date=${endDate}`,
  total_items_sold: (startDate, endDate) => `/inventory-activity?only-sales=1&start-date=${startDate}&end-date=${endDate}`,
  total_wastes: (startDate, endDate) => `/wastes?start-date=${startDate}&end-date=${endDate}`,
  invoice_sales: () => '/invoices',
  total_expenses: (startDate, endDate) => `/expenses?start-date=${startDate}&end-date=${endDate}`
};

export default {
  name: 'KpiGrid',

  props: {
    kpis: {
      type: Array,
      required: true
    },
    startDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      expanded: false
    };
  },

  computed: {
    kpisWithLinks() {
      return this.kpis.map(kpi => ({
        ...kpi,
        href: LINK_BUILDERS[kpi.key] ? LINK_BUILDERS[kpi.key](this.startDate, this.endDate) : null
      }));
    },

    primaryKpis() {
      const found = PRIMARY_ORDER
        .map(key => this.kpisWithLinks.find(k => k.key === key))
        .filter(Boolean);
      return found.length ? found : this.kpisWithLinks.slice(0, 6);
    },

    restKpis() {
      const primaryKeys = this.primaryKpis.map(k => k.key);
      return this.kpisWithLinks.filter(k => !primaryKeys.includes(k.key));
    },

    visibleKpis() {
      return this.expanded ? [...this.primaryKpis, ...this.restKpis] : this.primaryKpis;
    }
  },

  methods: {
    trendTextClass(kpi) {
      const positiveMetrics = ['total_sales', 'invoice_sales', 'gross_sales', 'net_sales', 'total_orders', 'gross_profit', 'net_profit'];
      const negativeMetrics = ['discount_total', 'refund_total', 'void_total', 'total_expenses', 'deductible_expenses', 'non_deductible_expenses', 'total_wastes'];
      const dir = kpi.comparison.direction;
      let sentiment = 'neutral';
      if (positiveMetrics.includes(kpi.key)) sentiment = dir === 'up' ? 'positive' : 'negative';
      else if (negativeMetrics.includes(kpi.key)) sentiment = dir === 'up' ? 'negative' : 'positive';
      return sentiment === 'positive' ? 'text-success' : sentiment === 'negative' ? 'text-danger' : 'text-secondary';
    }
  }
};
</script>

<style scoped>
.kpi-row--linked {
  display: block;
  text-decoration: none;
  border-radius: 8px;
  margin: -6px;
  padding: 6px;
  transition: background 0.15s;
}

.kpi-row--linked:hover {
  background: #f8fafc;
  text-decoration: none;
}

.kpi-row--linked:hover .kpi-row__value {
  text-decoration: underline;
}
</style>
